import { useGetUserDataQuery } from 'store/api/user';
import { t } from 'utils/getTranslations';
import { designInputsOf, useIsNewDesignOf } from 'utils/impersonationDesign';

/**
 * Whether the new design is on, for a form input: the design shown, so the one chosen in this browser while
 * impersonating. `useIsNewDesign` gives the same answer from the cache alone; an input asks through the query
 * `InputWrapper` already reads, so whatever renders a field needs nothing more for this. It re-renders the field
 * only when what the answer hangs on changes.
 */
export const useIsNewDesignInput = (): boolean =>
    useIsNewDesignOf(useGetUserDataQuery(undefined, { selectFromResult: ({ data }) => designInputsOf(data) }));

/**
 * The placeholder text a field shows, if it has one. The new design drops one that only repeats the label above
 * it: a placeholder is for an example of what to enter.
 */
export const useOwnPlaceholder = (
    placeholder: string | undefined,
    label: string | undefined,
    shouldTranslate: boolean
): string | undefined => {
    const isNewDesign = useIsNewDesignInput();
    if (!placeholder || (isNewDesign && placeholder === label)) {
        return undefined;
    }
    // A blank placeholder hides the default "Select" on a field that only displays a value; it has no message to translate.
    return shouldTranslate && placeholder.trim() ? t(placeholder) : placeholder;
};
